import { useState, useEffect, useMemo, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit3, Trash2, Filter, Download, Upload, Search, X, Save } from 'lucide-react';

const fINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

const TDB_CITIES = ['Bengaluru', 'Mumbai', 'Gurgaon', 'Online / B2B'];

/**
 * TDB Cost Entries tab — one row per invoice. Inline edit, delete, filter,
 * manual add, CSV download (no import for now — invoice scanner covers ingress).
 */
export default function TDBCostEntriesTab() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ city: '', category: '', date_from: '', date_to: '' });
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
      const [rRows, rCats] = await Promise.all([
        api.get(`/tdb/cost-entries${params ? '?' + params : ''}`),
        api.get('/tdb/cost-categories'),
      ]);
      setRows(rRows.data || []);
      setCategories(rCats.data || []);
    } catch (e) {
      toast.error('Failed to load cost entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);

  const total = useMemo(() => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0), [rows]);

  const startEdit = (r) => {
    setEditingId(r._id);
    setEditDraft({ ...r });
  };
  const saveEdit = async () => {
    try {
      await api.put(`/tdb/cost-entries/${editingId}`, editDraft);
      toast.success('Saved');
      setEditingId(null);
      setEditDraft(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };
  const deleteRow = async (id) => {
    if (!window.confirm('Delete this cost entry? This cannot be undone.')) return;
    try {
      await api.delete(`/tdb/cost-entries/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset so selecting the same file again re-triggers onChange
    e.target.value = '';
    const fd = new FormData();
    fd.append('file', file);
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await api.post('/tdb/cost-entries/import-csv', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(data);
      const { inserted = 0, updated = 0, skipped = 0 } = data || {};
      toast.success(`Imported ${inserted} new · ${updated} updated${skipped ? ` · ${skipped} skipped` : ''}`);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'CSV import failed';
      toast.error(typeof msg === 'string' ? msg : 'CSV import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="tdb-cost-entries-tab">
      {/* Filters + actions */}
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-wide block mb-0.5" style={{ color: '#9CA3AF' }}>City</label>
          <select value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })}
            className="rounded border px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }}
            data-testid="tdb-filter-city">
            <option value="">All cities</option>
            {TDB_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide block mb-0.5" style={{ color: '#9CA3AF' }}>Category</label>
          <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="rounded border px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }}
            data-testid="tdb-filter-category">
            <option value="">All</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide block mb-0.5" style={{ color: '#9CA3AF' }}>From</label>
          <input type="date" value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="rounded border px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }}
            data-testid="tdb-filter-from" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide block mb-0.5" style={{ color: '#9CA3AF' }}>To</label>
          <input type="date" value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="rounded border px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }}
            data-testid="tdb-filter-to" />
        </div>
        <button onClick={() => setFilters({ city: '', category: '', date_from: '', date_to: '' })}
          className="px-2 py-1.5 rounded text-xs hover:bg-[#F7F5F2]" style={{ color: '#6B7280' }}>
          Clear
        </button>

        <div className="ml-auto flex gap-2">
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium text-white"
            style={{ backgroundColor: '#2A7B88' }} data-testid="tdb-add-cost-btn">
            <Plus size={12} /> Add cost
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onImportFile}
            data-testid="tdb-cost-import-file"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs border hover:bg-[#F7F5F2] disabled:opacity-50"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
            title="Import cost entries from CSV"
            data-testid="tdb-cost-import-btn"
          >
            <Upload size={12} /> {importing ? 'Importing…' : 'Import CSV'}
          </button>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tdb/cost-entries/template-csv`}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs border hover:bg-[#F7F5F2]"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
            title="Download the blank CSV template"
            data-testid="tdb-cost-template-btn">
            <Download size={12} /> Template
          </a>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tdb/invoices/download-csv`}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs border hover:bg-[#F7F5F2]"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
            <Download size={12} /> Export CSV
          </a>
        </div>
      </div>

      {/* Import result banner */}
      {importResult && (
        <div className="rounded-lg border px-4 py-2.5 text-xs flex items-center gap-4"
          style={{ borderColor: '#E5E1DC', backgroundColor: (importResult.errors?.length || 0) ? '#FEF3E5' : '#ECFDF5' }}
          data-testid="tdb-cost-import-result">
          <span style={{ color: '#1B2A4A' }}>
            ✓ Imported <strong>{importResult.inserted || 0}</strong> new · updated <strong>{importResult.updated || 0}</strong>
            {importResult.skipped ? <> · skipped <strong>{importResult.skipped}</strong></> : null}
          </span>
          {importResult.errors?.length > 0 && (
            <details className="cursor-pointer" style={{ color: '#8A5A00' }}>
              <summary>{importResult.errors.length} row error{importResult.errors.length > 1 ? 's' : ''}</summary>
              <ul className="mt-1 ml-4 list-disc">
                {importResult.errors.slice(0, 10).map((er, i) => (
                  <li key={i}>Row {er.row}: {er.reason}</li>
                ))}
              </ul>
            </details>
          )}
          <button className="ml-auto" onClick={() => setImportResult(null)} style={{ color: '#9CA3AF' }}>
            <X size={12} />
          </button>
        </div>
      )}

      {/* Summary strip */}
      <div className="rounded-lg border px-4 py-2.5 flex items-baseline gap-4" style={{ borderColor: '#E5E1DC', backgroundColor: '#FAF8F5' }}>
        <div>
          <span className="text-[10px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Showing</span>{' '}
          <span className="font-semibold" style={{ color: '#1B2A4A' }}>{rows.length} entr{rows.length === 1 ? 'y' : 'ies'}</span>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Total</span>{' '}
          <span className="font-semibold font-mono" style={{ color: '#1B2A4A' }}>{fINR(total)}</span>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
        <table className="w-full text-xs" data-testid="tdb-cost-entries-table">
          <thead style={{ backgroundColor: '#FAF8F5' }}>
            <tr className="text-left" style={{ color: '#6B7280' }}>
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium">City</th>
              <th className="px-3 py-2 font-medium">Category</th>
              <th className="px-3 py-2 font-medium">Vendor</th>
              <th className="px-3 py-2 font-medium text-right">Amount</th>
              <th className="px-3 py-2 font-medium">Invoice #</th>
              <th className="px-3 py-2 font-medium">Notes</th>
              <th className="px-3 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" className="px-3 py-8 text-center" style={{ color: '#9CA3AF' }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="8" className="px-3 py-8 text-center" style={{ color: '#9CA3AF' }}>
                No cost entries match. Scan an invoice via Mira or click <strong>Add cost</strong>.
              </td></tr>
            )}
            {rows.map((r) => editingId === r._id ? (
              <EditRow key={r._id} draft={editDraft} setDraft={setEditDraft} categories={categories}
                onSave={saveEdit} onCancel={() => { setEditingId(null); setEditDraft(null); }} />
            ) : (
              <tr key={r._id} className="border-t" style={{ borderColor: '#F3F0EB' }}>
                <td className="px-3 py-2">{r.invoice_date || '—'}</td>
                <td className="px-3 py-2">{r.city || '—'}</td>
                <td className="px-3 py-2">{r.category || '—'}</td>
                <td className="px-3 py-2">{r.vendor_name || '—'}</td>
                <td className="px-3 py-2 text-right font-mono">{fINR(r.amount)}</td>
                <td className="px-3 py-2">{r.invoice_number || '—'}</td>
                <td className="px-3 py-2 max-w-[240px] truncate" title={r.notes}>{r.notes || '—'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => startEdit(r)} className="mr-2 hover:text-[#2A7B88]" title="Edit"
                    data-testid={`tdb-cost-edit-${r._id}`}>
                    <Edit3 size={12} />
                  </button>
                  <button onClick={() => deleteRow(r._id)} className="hover:text-[#B91C1C]" title="Delete"
                    data-testid={`tdb-cost-delete-${r._id}`}>
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddCostModal categories={categories} onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }} />
      )}
    </div>
  );
}

function EditRow({ draft, setDraft, categories, onSave, onCancel }) {
  const u = (k, v) => setDraft({ ...draft, [k]: v });
  return (
    <tr className="border-t" style={{ borderColor: '#F3F0EB', backgroundColor: '#FFFBEB' }}>
      <td className="px-2 py-1"><input type="date" value={(draft.invoice_date || '').slice(0, 10)}
        onChange={(e) => u('invoice_date', e.target.value)} className="w-full rounded border px-1 py-0.5 text-xs" /></td>
      <td className="px-2 py-1">
        <select value={draft.city || ''} onChange={(e) => u('city', e.target.value)}
          className="w-full rounded border px-1 py-0.5 text-xs">
          {TDB_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </td>
      <td className="px-2 py-1">
        <select value={draft.category || ''} onChange={(e) => u('category', e.target.value)}
          className="w-full rounded border px-1 py-0.5 text-xs">
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </td>
      <td className="px-2 py-1"><input value={draft.vendor_name || ''} onChange={(e) => u('vendor_name', e.target.value)}
        className="w-full rounded border px-1 py-0.5 text-xs" /></td>
      <td className="px-2 py-1"><input type="number" value={draft.amount || 0} onChange={(e) => u('amount', e.target.value)}
        className="w-full rounded border px-1 py-0.5 text-xs text-right" /></td>
      <td className="px-2 py-1"><input value={draft.invoice_number || ''} onChange={(e) => u('invoice_number', e.target.value)}
        className="w-full rounded border px-1 py-0.5 text-xs" /></td>
      <td className="px-2 py-1"><input value={draft.notes || ''} onChange={(e) => u('notes', e.target.value)}
        className="w-full rounded border px-1 py-0.5 text-xs" /></td>
      <td className="px-2 py-1 text-right">
        <button onClick={onSave} className="mr-2" title="Save" data-testid="tdb-cost-save-edit"><Save size={12} style={{ color: '#059669' }} /></button>
        <button onClick={onCancel} title="Cancel"><X size={12} style={{ color: '#9CA3AF' }} /></button>
      </td>
    </tr>
  );
}

function AddCostModal({ categories, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [d, setD] = useState({
    invoice_date: today, city: 'Bengaluru', category: categories[0] || 'Gas & Fuel',
    vendor_name: '', amount: '', invoice_number: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!d.amount || Number(d.amount) <= 0) { toast.error('Amount required'); return; }
    setSaving(true);
    try {
      await api.post('/tdb/cost-entries', d);
      toast.success('Cost entry added');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };
  const upd = (k, v) => setD({ ...d, [k]: v });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}
      data-testid="tdb-add-cost-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold mb-4" style={{ color: '#1B2A4A' }}>Add cost entry</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date"><input type="date" value={d.invoice_date} onChange={(e) => upd('invoice_date', e.target.value)}
            className="w-full rounded border px-2 py-1.5 text-sm" data-testid="tdb-new-date" /></Field>
          <Field label="City">
            <select value={d.city} onChange={(e) => upd('city', e.target.value)}
              className="w-full rounded border px-2 py-1.5 text-sm" data-testid="tdb-new-city">
              {TDB_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Category">
            <select value={d.category} onChange={(e) => upd('category', e.target.value)}
              className="w-full rounded border px-2 py-1.5 text-sm" data-testid="tdb-new-category">
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Amount (₹)">
            <input type="number" value={d.amount} onChange={(e) => upd('amount', e.target.value)}
              placeholder="0" className="w-full rounded border px-2 py-1.5 text-sm" data-testid="tdb-new-amount" />
          </Field>
          <Field label="Vendor" span={2}>
            <input value={d.vendor_name} onChange={(e) => upd('vendor_name', e.target.value)}
              placeholder="e.g. Blue Dart" className="w-full rounded border px-2 py-1.5 text-sm" data-testid="tdb-new-vendor" />
          </Field>
          <Field label="Invoice #" span={2}>
            <input value={d.invoice_number} onChange={(e) => upd('invoice_number', e.target.value)}
              className="w-full rounded border px-2 py-1.5 text-sm" data-testid="tdb-new-invoiceno" />
          </Field>
          <Field label="Notes" span={2}>
            <textarea value={d.notes} onChange={(e) => upd('notes', e.target.value)} rows={2}
              className="w-full rounded border px-2 py-1.5 text-sm" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-xs border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            className="px-3 py-1.5 rounded text-xs font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: '#2A7B88' }} data-testid="tdb-new-save">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, span = 1, children }) {
  return (
    <div className={span === 2 ? 'col-span-2' : ''}>
      <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>{label}</label>
      {children}
    </div>
  );
}
